<template>
  <div class="property-editor">
    <v-form v-model="valid" ref="form" lazy-validation>
      <!-- STICKY ACTION BAR -->
      <v-sheet class="sticky-actions pa-3">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon class="mr-2" small>mdi-home-city</v-icon>

            <div class="d-flex flex-column">
              <div class="text-subtitle-2 font-weight-bold">
                {{ (propertyData && propertyData.title && propertyData.title.HR) || 'Nova nekretnina' }}
              </div>

              <div class="caption grey--text">
                <span v-if="property">
                  Zadnje promjene: {{ new Date(property.updatedAt) | formatDate }}
                </span>
                <span class="ml-2" v-if="hasChanged">• Nespremljene promjene</span>
                <span class="ml-2 error--text" v-if="!valid">• Ima grešaka u formi</span>
              </div>
            </div>
          </div>

          <div class="d-flex align-center">
            <v-btn
              v-if="propertyData.id"
              @click="showDeleteDialog = true"
              text
              color="red darken-2"
              class="mr-2"
            >
              Arhiviraj
            </v-btn>

            <v-btn
              :disabled="isDisabledSave"
              color="success"
              class="px-8"
              @click="save"
            >
              {{ propertyData.id ? 'Spremi promjene' : 'Dodaj nekretninu' }}
            </v-btn>
          </div>
        </div>

        <div class="mt-2" v-if="showPhotoUploadWarning || showDocumentUploadWarning">
          <div v-if="showPhotoUploadWarning" class="red--text text--darken-2 text-caption">
            Upozorenje: Odabrali ste fotografije koje niste uploadali!
          </div>
          <div v-if="showDocumentUploadWarning" class="red--text text--darken-2 text-caption">
            Upozorenje: Odabrali ste dokumente koje niste uploadali!
          </div>
        </div>
      </v-sheet>

      <!-- LAYOUT -->
      <v-row class="mt-4" dense>
        <!-- SIDEBAR -->
        <v-col v-if="$vuetify.breakpoint.lgAndUp" cols="12" lg="2">
          <v-card class="pa-2 sidebar-card">
            <v-list dense nav>
              <v-list-item @click="goTo('#sec-basic')">
                <v-list-item-icon><v-icon small>mdi-information</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Osnovno</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item @click="goTo('#sec-location')">
                <v-list-item-icon><v-icon small>mdi-map-marker</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Lokacija i površina</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item @click="goTo('#sec-map')">
                <v-list-item-icon><v-icon small>mdi-map</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Karta</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item v-if="!isLandPlot" @click="goTo('#sec-building')">
                <v-list-item-icon><v-icon small>mdi-home</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Objekt</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item @click="goTo('#sec-partner')">
                <v-list-item-icon><v-icon small>mdi-account-circle</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Partner i agent</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item @click="goTo('#sec-permits')">
                <v-list-item-icon><v-icon small>mdi-check-decagram</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Dozvole</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item @click="goTo('#sec-registry')">
                <v-list-item-icon><v-icon small>mdi-text-box-search</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Katastar i ZK</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item @click="goTo('#sec-media')">
                <v-list-item-icon><v-icon small>mdi-image-multiple</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Media</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item @click="goTo('#sec-contracts')">
                <v-list-item-icon><v-icon small>mdi-file-multiple</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Ugovori</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item @click="goTo('#sec-docs')">
                <v-list-item-icon><v-icon small>mdi-file-table-box-multiple</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Ostali dokumenti</v-list-item-title></v-list-item-content>
              </v-list-item>

              <v-list-item @click="goTo('#sec-publish')">
                <v-list-item-icon><v-icon small>mdi-eye</v-icon></v-list-item-icon>
                <v-list-item-content><v-list-item-title>Objava</v-list-item-title></v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>

        <!-- CONTENT -->
        <v-col :lg="$vuetify.breakpoint.lgAndUp ? 10 : 12" cols="12">
          <!-- BASIC -->
          <section id="sec-basic" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-information</v-icon>
              <span>Osnovni podaci</span>
            </div>

            <v-divider class="my-3" />

            <!-- 1) NAZIV -->
            <translation-tabs
              @select="activeTitleLanguage = $event"
              :missingTranslations="getMissingTranslations(propertyData.title)"
              class="mb-2"
            />

            <v-row dense class="mt-2">
              <v-col cols="12">
                <v-text-field
                  v-model="propertyData.title[activeTitleLanguage]"
                  :rules="[v => !!v || requiredMessage]"
                  autocomplete="off"
                  class="font-weight-bold"
                  clearable
                  label="Naziv"
                  required
                />
              </v-col>
            </v-row>

            <v-row dense>
              <v-col cols="12" class="text-right">
                <v-btn
                  @click="translate('title')"
                  :disabled="isDisabledTitleTranslation"
                  :loading="isLoadingTitleTranslation"
                  class="mb-2"
                  text
                >
                  Dohvati prijevod za {{ activeTitleLanguageFull }}
                  <v-icon right>mdi-web</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <!-- 2) KATEGORIJA + TIP -->
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="propertyData.category"
                  :items="categories"
                  :rules="[v => !!v || requiredMessage]"
                  item-text="label"
                  label="Kategorija"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                  v-model="propertyData.propertyType"
                  :items="propertyTypes"
                  :disabled="!propertyData.category"
                  :rules="[v => !!v || requiredMessage]"
                  item-text="label"
                  label="Tip nekretnine"
                />
              </v-col>
            </v-row>

            <!-- 3) CIJENE -->
            <v-row dense>
              <v-col cols="12" md="2">
                <vuetify-money
                  v-model="propertyData.price"
                  label="Cijena"
                  class="font-weight-bold"
                  :options="{ locale: 'us-US', precision: 0, suffix: '€' }"
                />
              </v-col>

              <v-col cols="12" md="2">
                <vuetify-money
                  v-model="propertyData.actualPrice"
                  label="Stvarna cijena"
                  :options="{ locale: 'us-US', precision: 0, suffix: '€' }"
                />
              </v-col>

              <v-col cols="12" md="2">
                <vuetify-money
                  v-model="propertyData.priceM2"
                  label="Cijena/m²"
                  :options="{ locale: 'us-US', precision: 0, suffix: '€' }"
                />
              </v-col>

              <v-col cols="12" md="3">
                <vuetify-money
                  v-model="propertyData.evaluatedPrice"
                  label="Procjena nekretnine (službena)"
                  :options="{ locale: 'us-US', precision: 0, suffix: '€' }"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="propertyData.taxStatus"
                  @input="validateNull('taxStatus')"
                  :items="taxTypes"
                  clearable
                  item-text="label"
                  label="Porezni status"
                />
              </v-col>
            </v-row>

            <!-- 4) CIJENA NA UPIT -->
            <v-row dense>
              <v-col cols="12" class="d-flex align-center">
                <v-checkbox
                  v-model="propertyData.priceOnRequest"
                  color="lime darken-4"
                  class="input-lime-darken-4 mr-2"
                  label="Cijena na upit"
                />
                <info-tooltip
                  color="lime darken-4"
                  text="Prikaži/sakrij informaciju o cijeni"
                />
              </v-col>
            </v-row>

            <!-- 5) OPISI (KRATKI + DETALJAN U ISTOM RETKU) -->
            <v-row>
              <!-- KRATKI -->
              <v-col cols="12" md="6">
                <div class="d-flex justify-space-between align-center mb-2">
                  <label class="small-label">
                    Kratki opis (javno dostupno svim korisnicima)
                  </label>

                  <translation-tabs
                    @select="activeShortDescriptionLanguage = $event"
                    :missingTranslations="getMissingTranslations(propertyData.shortDescription)"
                  />
                </div>

                <text-editor
                  v-model="propertyData.shortDescription[activeShortDescriptionLanguage]"
                  placeholder="Kratki opis"
                />

                <div class="d-flex justify-end">
                  <v-btn
                    @click="translate('shortDescription')"
                    :disabled="isDisabledShortDescriptionTranslation"
                    :loading="isLoadingShortDescriptionTranslation"
                    class="mt-2"
                    text
                  >
                    Dohvati prijevod za {{ activeShortDescriptionLanguageFull }}
                    <v-icon right>mdi-web</v-icon>
                  </v-btn>
                </div>
              </v-col>

              <!-- DETALJAN -->
              <v-col cols="12" md="6">
                <div class="d-flex justify-space-between align-center mb-2">
                  <label class="small-label">
                    Detaljan opis (vidljivo uz odobrenje)
                  </label>

                  <translation-tabs
                    @select="activeDescriptionLanguage = $event"
                    :missingTranslations="getMissingTranslations(propertyData.description)"
                  />
                </div>

                <text-editor
                  v-model="propertyData.description[activeDescriptionLanguage]"
                  placeholder="Detaljan opis"
                />

                <div class="d-flex justify-end">
                  <v-btn
                    @click="translate('description')"
                    :disabled="isDisabledDescriptionTranslation"
                    :loading="isLoadingDescriptionTranslation"
                    class="mt-2"
                    text
                  >
                    Dohvati prijevod za {{ activeDescriptionLanguageFull }}
                    <v-icon right>mdi-web</v-icon>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </section>

          <!-- LOCATION -->
          <section id="sec-location" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-map-marker</v-icon>
              <span>Lokacija i površina</span>
            </div>
            <v-divider class="my-3" />

            <v-row dense>
              <v-col cols="12" md="4">
                <v-select
                  v-model="propertyData.countryId"
                  :items="countries"
                  :rules="[v => !!v || requiredMessage]"
                  item-text="label"
                  item-value="id"
                  label="Država"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                  v-model="propertyData.regionId"
                  @input="onRegionChange"
                  :items="regions"
                  clearable
                  item-text="name"
                  item-value="id"
                  label="Regija"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                  v-model="propertyData.islandId"
                  @input="validateNull('islandId')"
                  :items="islands"
                  clearable
                  item-text="name"
                  item-value="id"
                  label="Otok"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                  v-model="propertyData.countyId"
                  @input="onCountyChange"
                  :items="counties"
                  clearable
                  item-text="name"
                  item-value="id"
                  label="Županija"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                  v-model="propertyData.municipalityId"
                  @input="onMunicipalityChange"
                  :disabled="!propertyData.countyId"
                  :items="municipalities"
                  clearable
                  item-text="name"
                  item-value="id"
                  label="Općina"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                  v-model="propertyData.cityId"
                  @input="validateNull('cityId')"
                  :disabled="!propertyData.municipalityId"
                  :items="cities"
                  clearable
                  item-text="name"
                  item-value="id"
                  label="Mjesto"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="propertyData.quarter"
                  :disabled="!propertyData.cityId"
                  label="Uža lokacija"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="propertyData.address"
                  :disabled="!propertyData.quarter"
                  label="Adresa"
                />
              </v-col>

              <v-col cols="12" md="4">
                <vuetify-money
                  v-model="propertyData.surface"
                  :class="{ 'font-weight-bold': !isLandPlot }"
                  label="Površina objekta"
                  :options="{ locale: 'us-US', precision: 0, suffix: 'm²' }"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-select
                  v-model="propertyData.surfaceType"
                  @input="validateNull('surfaceType')"
                  :items="surfaceTypes"
                  clearable
                  item-text="label"
                  label="Brutto/Netto"
                />
              </v-col>

              <v-col cols="12" md="4">
                <vuetify-money
                  v-model="propertyData.plotSurface"
                  :class="{ 'font-weight-bold': isLandPlot }"
                  label="Površina zemljišta"
                  :options="{ locale: 'us-US', precision: 0, suffix: 'm²' }"
                />
              </v-col>

              <v-col cols="12" md="4">
                <vuetify-money
                  v-model="propertyData.seaDistance"
                  label="Udaljenost od mora"
                  :options="{ locale: 'us-US', precision: 0, suffix: 'm' }"
                />
              </v-col>

              <v-col cols="12" md="4">
                <vuetify-money
                  v-model="propertyData.beachDistance"
                  label="Udaljenost od plaže"
                  :options="{ locale: 'us-US', precision: 0, suffix: 'm' }"
                />
              </v-col>

              <v-col cols="12" md="4">
                <vuetify-money
                  v-model="propertyData.cityCentreDistance"
                  label="Udaljenost od centra"
                  :options="{ locale: 'us-US', precision: 0, suffix: 'm' }"
                />
              </v-col>

              <v-col cols="12" md="4"><v-checkbox v-model="propertyData.seafront" label="Prvi red do mora" /></v-col>
              <v-col cols="12" md="4"><v-checkbox v-model="propertyData.seaView" label="Pogled na more" /></v-col>
              <v-col cols="12" md="4"><v-checkbox v-model="propertyData.panoramicView" label="Panoramski pogled" /></v-col>
            </v-row>
          </section>

          <!-- MAP -->
          <section id="sec-map" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-map</v-icon>
              <span>Lokacija na karti</span>
            </div>
            <v-divider class="my-3" />

            <v-row dense>
              <v-col cols="12" md="3">
                <v-text-field v-model="propertyData.coordinates.lat" disabled label="Koordinate (LAT)" />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="propertyData.coordinates.lng" disabled label="Koordinate (LNG)" />
              </v-col>

              <v-col cols="12" md="3">
                <v-btn @click="showGoogleMapDialog = true" width="100%">
                  {{ hasCoordinates ? 'Uredi' : 'Označi' }} lokaciju na karti
                  <v-icon class="ml-2" size="20" color="success">mdi-map-marker</v-icon>
                </v-btn>
              </v-col>

              <v-col cols="12" md="3">
                <v-btn @click="removeCoordinates" :disabled="!hasCoordinates" width="100%">
                  Ukloni lokaciju
                  <v-icon class="ml-2" size="20" color="error">mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <google-map-dialog
              v-if="showGoogleMapDialog"
              v-model="showGoogleMapDialog"
              @updateCoordinates="updateCoordinates"
              :initialMarkerPosition="initialMarkerPosition"
            />
          </section>

          <!-- BUILDING -->
          <section v-if="!isLandPlot" id="sec-building" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-home</v-icon>
              <span>Informacije o objektu</span>
            </div>
            <v-divider class="my-3" />

            <v-row dense>
              <v-col cols="12" md="3"><v-text-field v-model.number="propertyData.floor" label="Kat" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="propertyData.floorsTotal" label="Ukupno katova" type="number" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.newConstruction" label="Novogradnja" /></v-col>

              <v-col cols="12" md="3"><v-text-field v-model.number="propertyData.rooms" label="Broj prostorija" type="number" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="propertyData.bedrooms" label="Broj spavaćih soba" type="number" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="propertyData.bathrooms" label="Broj kupaona" type="number" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="propertyData.toilets" label="Broj toaleta" type="number" /></v-col>

              <v-col cols="12" md="3"><v-combobox v-model.number="propertyData.constructionYear" :items="years" label="Godina izgradnje" type="number" /></v-col>
              <v-col cols="12" md="3"><datepicker v-model="propertyData.constructionDeadline" placeholder="Rok završetka" /></v-col>
              <v-col cols="12" md="3"><v-combobox v-model.number="propertyData.adaptationYear" :items="years" label="Godina adaptacije" type="number" /></v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="propertyData.condition"
                  @input="validateNull('condition')"
                  :items="conditions"
                  item-text="label"
                  clearable
                  label="Stanje"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="propertyData.energyCertificate"
                  @input="validateNull('energyCertificate')"
                  :items="['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G']"
                  clearable
                  label="Energetska učinkovitost (EC)"
                />
              </v-col>

              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.heating" label="Grijanje" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.cooling" label="Hlađenje" /></v-col>

              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.parking" label="Parking" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.garageParking" label="Garažno mjesto" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.elevator" label="Lift" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.swimmingPool" label="Bazen" /></v-col>

              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.gym" label="Teretana" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.jacuzzi" label="Jacuzzi" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.sauna" label="Sauna" /></v-col>
            </v-row>

            <div class="section-title mt-6">
              <v-icon small class="mr-2">mdi-text-box-outline</v-icon>
              <span>Ostale informacije</span>
            </div>
            <v-divider class="my-3" />

            <v-row dense>
              <v-col cols="12" md="3">
                <vuetify-money v-model="propertyData.mortgage" label="Otkup/hipoteka" :options="{ locale: 'us-US', precision: 0, suffix: '€' }" />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="propertyData.transferRiskLevel"
                  @input="validateNull('transferRiskLevel')"
                  :items="transferRiskLevels"
                  clearable
                  item-text="label"
                  label="Stupanj rizičnosti transkacije"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-checkbox v-model="propertyData.possession" label="Posjed nekretnine" />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="propertyData.orientation"
                  @input="validateNull('orientation')"
                  :items="orientations"
                  clearable
                  multiple
                  item-text="label"
                  label="Orijentacija"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="propertyData.infrastructure"
                  @input="validateNull('infrastructure')"
                  :items="infrastructure"
                  clearable
                  multiple
                  item-text="label"
                  label="Osnovna infrastruktura"
                />
              </v-col>
            </v-row>
          </section>

          <!-- PARTNER -->
          <section id="sec-partner" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-account-circle</v-icon>
              <span>Partner i agent</span>
            </div>
            <v-divider class="my-3" />

            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  v-model="propertyData.partnerId"
                  @input="validateNull('partnerId')"
                  :items="partners"
                  clearable
                  item-text="name"
                  item-value="id"
                  label="Partner"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="propertyData.agentId"
                  @input="validateNull('agentId')"
                  :items="agents"
                  clearable
                  item-value="id"
                  label="Agent"
                >
                  <template slot="selection" slot-scope="data">
                    {{ data.item.firstName }} {{ data.item.lastName }}
                  </template>
                  <template slot="item" slot-scope="data">
                    {{ data.item.firstName }} {{ data.item.lastName }}
                  </template>
                </v-select>
              </v-col>
            </v-row>
          </section>

          <!-- PERMITS -->
          <section id="sec-permits" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-check-decagram</v-icon>
              <span>Dozvole</span>
            </div>
            <v-divider class="my-3" />

            <v-row dense>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.buildingPermit" label="Građevinska dozvola" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.locationPermit" label="Lokacijska dozvola" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.usagePermit" label="Uporabna dozvola" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.derivedConditionSolution" label="Rješenje o izvedenom stanju" /></v-col>

              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.ownershipCertificate" label="Vlasnički list" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.condominium" label="Etažirano" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.condominiumInProcess" label="Etažiranje u postupku" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.usagePermitInProcess" label="Uporabna dozvola u postupku" /></v-col>

              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.bookOfDepositedContracts" label="Knjiga položenih ugovora" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.minimumTechnicalRequirements" label="Minimalni tehnički uvjeti" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.legalizationInProcess" label="Legalizacija u postupku" /></v-col>
              <v-col cols="12" md="3"><v-checkbox v-model="propertyData.possessionInProcess" label="Posjed u postupku" /></v-col>
            </v-row>
          </section>

          <!-- REGISTRY -->
          <section id="sec-registry" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-text-box-search</v-icon>
              <span>Katastar i zemljišnik</span>
            </div>
            <v-divider class="my-3" />

            <v-row dense>
              <v-col cols="12" md="4"><v-text-field v-model="propertyData.landRegistryExtract" label="Broj ZK uloška" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="propertyData.cadastreMunicipality" label="Katastarska općina" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="propertyData.cadastreParcelNumber" label="Broj katastarske čestice" /></v-col>

              <v-col cols="12" md="8">
                <v-text-field
                  v-model="propertyData.externalLinks.katastarHr"
                  label="Poveznica katastar.hr"
                  prepend-icon="mdi-link-variant"
                />
              </v-col>
            </v-row>
          </section>

          <!-- MEDIA TABS -->
          <section id="sec-media" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-image-multiple</v-icon>
              <span>Media</span>
            </div>
            <v-divider class="my-3" />

            <v-card class="pa-3">
              <v-tabs v-model="mediaTab" grow>
                <v-tab>Fotografije</v-tab>
                <v-tab>Video</v-tab>
                <v-tab>Virtualna šetnja</v-tab>
              </v-tabs>

              <v-tabs-items v-model="mediaTab">
                <v-tab-item>
                  <v-row dense class="mt-2">
                    <v-col cols="12">
                      <file-upload
                        @showWarning="showPhotoUploadWarning = $event"
                        @uploaded="storePhoto"
                        :inputId="'upload-photo'"
                        :maxSize="10"
                        :propertyId="this.propertyId"
                        label="Dovucite fotografije ovdje"
                      />
                    </v-col>
                  </v-row>
                  <property-gallery v-model="propertyData.photos" />
                </v-tab-item>

                <v-tab-item>
                  <v-row dense class="mt-2">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="propertyData.video"
                        clearable
                        label="Video URL (Youtube)"
                        prepend-icon="mdi-link"
                      />
                    </v-col>
                  </v-row>
                  <video-player v-if="propertyData.video" :videoUrl="propertyData.video" />
                </v-tab-item>

                <v-tab-item>
                  <v-row dense class="mt-2">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="propertyData.virtualTour"
                        clearable
                        label="Virtual Tour URL"
                        prepend-icon="mdi-link"
                      />
                    </v-col>
                  </v-row>
                  <virtual-tour v-if="propertyData.virtualTour" :tourUrl="propertyData.virtualTour" />
                </v-tab-item>
              </v-tabs-items>
            </v-card>
          </section>

          <!-- CONTRACTS -->
          <section id="sec-contracts" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-file-multiple</v-icon>
              <span>Ugovori</span>
            </div>
            <v-divider class="my-3" />

            <v-row dense>
              <v-col cols="12" md="6">
                <contract-upload
                  v-model="propertyData.contracts.mediationAgreement"
                  @delete="setContract('mediationAgreement', null)"
                  @uploaded="setContract('mediationAgreement', $event)"
                  :label="'Ugovor o posredovanju'"
                  :propertyId="propertyId"
                />
              </v-col>

              <v-col cols="12" md="6">
                <contract-upload
                  v-model="propertyData.contracts.NDA"
                  @delete="setContract('NDA', null)"
                  @uploaded="setContract('NDA', $event)"
                  :label="'NDA'"
                  :propertyId="propertyId"
                />
              </v-col>
            </v-row>
          </section>

          <!-- OTHER DOCS -->
          <section id="sec-docs" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-file-table-box-multiple</v-icon>
              <span>Ostali dokumenti</span>
            </div>
            <v-divider class="my-3" />

            <v-card class="pa-3">
              <v-row dense>
                <v-col cols="12">
                  <file-upload
                    @showWarning="showDocumentUploadWarning = $event"
                    @uploaded="storeDocument"
                    :propertyId="this.propertyId"
                    :maxSize="300"
                    :inputId="'upload-document'"
                    accept="DOCUMENTS"
                    label="Dovucite dokumente ovdje"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <other-documents v-model="propertyData.documents" />
                </v-col>
              </v-row>
            </v-card>
          </section>

          <!-- PUBLISH -->
          <section id="sec-publish" class="section">
            <div class="section-title">
              <v-icon small class="mr-2">mdi-eye</v-icon>
              <span>Objava</span>
            </div>
            <v-divider class="my-3" />

            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  v-model="propertyData.published"
                  :items="publishedOptions"
                  item-text="label"
                  label="Objavi na portalu"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="propertyData.featured"
                  :items="featuredOptions"
                  item-text="label"
                  label="Istaknuto"
                />
              </v-col>
            </v-row>

            <delete-modal
              v-if="propertyData.id"
              @cancel="showDeleteDialog = false"
              @confirm="archive"
              :show="showDeleteDialog"
              :title="`Arhiviraj nekretninu ${property.publicId}`"
              confirmLabel="Arhiviraj"
              hideDeleteIcon
              subtitle="Jeste li sigurni da želite arhivirati ovu nekretninu?"
            />
          </section>

          <div style="height: 24px;"></div>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import api from '../../../api/property';
import category from 'common/property/category';
import ContractUpload from './documents/ContractUpload';
import Datepicker from '../../common/Datepicker';
import DeleteModal from '../../common/DeleteModal';
import FileUpload from '../../common/FileUpload';
import get from 'lodash/get';
import GoogleMapDialog from './GoogleMapDialog';
import InfoTooltip from './common/InfoTooltip';
import isEqual from 'lodash/isEqual';
import language from 'common/language';
import OtherDocuments from './documents/OtherDocuments';
import PropertyGallery from './Gallery';
import { propertyMixin } from './property-mixin';
import propertyType from 'common/property/type';
import pull from 'lodash/pull';
import reduce from 'lodash/reduce';
import reverse from 'lodash/reverse';
import range from 'lodash/range';
import TextEditor from '../../common/TextEditor';
import TranslationTabs from '../../common/TranslationTabs';
import VideoPlayer from './VideoPlayer';
import VirtualTour from './VirtualTour';
import Vue from 'vue';

function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export default {
  name: 'property-section',
  mixins: [propertyMixin],
  props: {
    propertyId: { type: Number, default: null }
  },
  data: () => ({
    activeTitleLanguage: 'HR',
    activeDescriptionLanguage: 'HR',
    activeShortDescriptionLanguage: 'HR',

    isLoadingDescriptionTranslation: false,
    isLoadingShortDescriptionTranslation: false,
    isLoadingTitleTranslation: false,

    showDeleteDialog: false,
    showDocumentUploadWarning: false,
    showGoogleMapDialog: false,
    showPhotoUploadWarning: false,

    valid: true,

    // Media tabs (0: photos, 1: video, 2: virtual)
    mediaTab: 0
  }),
  computed: {
    ...mapGetters('translations', [
      'categories',
      'commercial',
      'conditions',
      'countries',
      'infrastructure',
      'orientations',
      'residential',
      'surfaceTypes',
      'transferRiskLevels',
      'taxTypes'
    ]),
    ...mapState('properties', ['property']),

    activeDescriptionLanguageFull() {
      return language[this.activeDescriptionLanguage];
    },
    activeShortDescriptionLanguageFull() {
      return language[this.activeShortDescriptionLanguage];
    },
    activeTitleLanguageFull() {
      return language[this.activeTitleLanguage];
    },

    hasChanged() {
      return !isEqual(this.propertyData, this.initialPropertyData);
    },

    hasCoordinates() {
      return !!this.propertyData.coordinates &&
        !!this.propertyData.coordinates.lat &&
        !!this.propertyData.coordinates.lng;
    },

    initialMarkerPosition() {
      return get(this.propertyData, 'coordinates');
    },

    isDisabledDescriptionTranslation() {
      return this.activeDescriptionLanguage === 'HR' ||
        !!this.propertyData.description[this.activeDescriptionLanguage]
    },
    isDisabledShortDescriptionTranslation() {
      return this.activeShortDescriptionLanguage === 'HR' ||
        !!this.propertyData.shortDescription[this.activeShortDescriptionLanguage]
    },
    isDisabledTitleTranslation() {
      return this.activeTitleLanguage === 'HR' ||
        !!this.propertyData.title[this.activeTitleLanguage]
    },

    isDisabledSave() {
      return !this.valid ||
        !this.hasChanged ||
        this.showPhotoUploadWarning ||
        this.showDocumentUploadWarning;
    },

    isLandPlot() {
      const { COMMERCIAL_LAND_PLOT, LAND_PLOT } = propertyType;
      return this.propertyData.propertyType === COMMERCIAL_LAND_PLOT.value ||
        this.propertyData.propertyType === LAND_PLOT.value
    },

    propertyTypes() {
      return this.propertyData.category === category.COMMERCIAL.value
        ? this.commercial
        : this.residential
    },

    publishedOptions() {
      return [
        { label: 'Sakriveno', value: false },
        { label: 'Objavljeno', value: true }
      ];
    },
    featuredOptions() {
      return [
        { label: 'Ne', value: false },
        { label: 'Da', value: true }
      ];
    },

    requiredMessage() {
      return 'Ovo polje je obavezno';
    },

    years() {
      const years = range(1950, new Date().getFullYear() + 1);
      return reverse(years);
    }
  },
  methods: {
    ...mapActions('properties', ['create', 'update']),

    goTo(selector) {
      const el = document.querySelector(selector);
      if (!el) return;
      // sticky header + a bit of breathing room
      this.$vuetify.goTo(el, { offset: 90 });
    },

    async addProperty() {
      const property = await this.create(this.propertyData);
      this.$router.push({
        name: 'edit-property',
        params: { propertyId: property.id }
      });
    },

    archive() {
      return api.remove(this.property.id)
        .then(() => this.$router.push({ name: 'home' }));
    },

    getMissingTranslations(field) {
      const languages = Object.keys(language);
      const translations = reduce(field, (acc, value, key) => {
        if (value) acc.push(key);
        return acc;
      }, []);
      return pull(languages, ...translations);
    },

    removeCoordinates() {
      this.propertyData.coordinates.lat = null;
      this.propertyData.coordinates.lng = null;
    },

    async save() {
      this.$refs.form.validate();

      this.$nextTick(async () => {
        if (!this.valid) return this.scrollToError();
        return this.propertyId ? this.updateProperty() : this.addProperty();
      });
    },

    scrollToError() {
      const el = this.$el.querySelector('.v-messages.error--text:first-of-type');
      if (!el) return;
      this.$vuetify.goTo(el, { offset: 110 });
    },

    setContract(contractName, data) {
      this.propertyData.contracts[contractName] = data;
    },

    storeDocument(data) {
      this.propertyData.documents.push(data);
    },

    storePhoto(data) {
      this.propertyData.photos.push(data);
    },

    async translate(field) {
      const capitalized = capitalize(field);
      this[`isLoading${capitalized}Translation`] = true;

      const lang = this[`active${capitalized}Language`] === 'SI'
        ? 'sl'
        : this[`active${capitalized}Language`].toLowerCase();

      const params = {
        Text: this.propertyData[field].HR,
        SourceLanguageCode: 'hr',
        TargetLanguageCode: lang
      };

      const { TranslatedText: translation } = await api.getTranslation(params);
      const activeLang = this[`active${capitalized}Language`];

      Vue.set(this.propertyData[field], activeLang, translation);

      this[`isLoading${capitalized}Translation`] = false;
    },

    updateCoordinates(coordinates) {
      this.propertyData.coordinates = coordinates;
    },

    async updateProperty() {
      await this.update(this.propertyData);
    }
  },
  watch: {
    // Keep your existing behavior
    isLandPlot(val) {
      // TODO: Reset all building fields
      if (!val) {
        this.rooms = null;
      }
    },

    activeDescriptionLanguage: {
      immediate: true,
      handler(lang) {
        if (!this.propertyData.description[lang]) {
          Vue.set(this.propertyData.description, lang, '');
        }
      }
    },

    activeShortDescriptionLanguage: {
      immediate: true,
      handler(lang) {
        if (!this.propertyData.shortDescription[lang]) {
          Vue.set(this.propertyData.shortDescription, lang, '');
        }
      }
    }
  },
  components: {
    ContractUpload,
    Datepicker,
    DeleteModal,
    FileUpload,
    GoogleMapDialog,
    InfoTooltip,
    OtherDocuments,
    PropertyGallery,
    TextEditor,
    TranslationTabs,
    VideoPlayer,
    VirtualTour
  }
};
</script>

<style scoped lang="scss">
/* Layout wrapper */
.property-editor {
  padding-bottom: 24px;
}

/* Sticky top action bar */
.sticky-actions {
  position: sticky;
  top: 188px;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
}

/* Sticky sidebar under top bar */
.sidebar-card {
  position: sticky;
  top: 274px; /* adjust if your top bar height differs */
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
}

/* Section blocks */
.section {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
}

/* Section header */
.section-title {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 14px;
}

/* Small labels used near translation tabs */
.small-label {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
}

/* Make v-list items feel a bit tighter/cleaner */
::v-deep .sidebar-card .v-list-item {
  border-radius: 8px;
}
::v-deep .sidebar-card .v-list-item__title {
  font-size: 13px;
}
::v-deep .sidebar-card .v-list-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

/* Keep your custom lime styling */
::v-deep .input-lime-darken-4 {
  label,
  .v-icon {
    color: #827717;
  }
}

/* Text editor height (CKEditor) */
::v-deep {
  .ck-content.ck-editor__editable {
    min-height: 220px;
  }
}

/* Slightly calmer tabs (optional) */
::v-deep .v-tabs-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* Better spacing for buttons under editors */
::v-deep .v-btn {
  letter-spacing: normal;
}

/* Mobile tweaks */
@media (max-width: 1264px) {
  .sidebar-card {
    position: static;
    top: auto;
    margin-bottom: 12px;
  }
  .sticky-actions {
    position: sticky;
    top: 64px;
  }
}

// /* Mobile */
// @media (max-width: 960px) {
//   .sticky-actions {
//     top: 96px;
//   }
// }
</style>
